import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Grid, Segmented, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClusterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import {
  getFoundryDeployments,
  type FoundryDeploymentRow,
  type FoundryDeploymentsOverview,
  type FoundryStatusKey,
  type FoundryTone,
} from '@/api/foundry';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Foundry → Deployments. READ-ONLY: what the Azure AI Foundry project has
// deployed, and which platform rows name each deployment. Nothing here deploys,
// changes or removes anything; catalog search and deploy are a later step.
//
// A deployment nothing names is "Not used", never "missing from the registry":
// ai.AIModel is the chat-model registry, and an embedding or OCR deployment there
// would be offered to agents and baked into Foundry agent versions. Every
// sentence on screen comes from FoundryDeploymentRules on the server.
// ─────────────────────────────────────────────────────────────────────────────

type Filter = 'all' | 'in-use' | 'switched-off' | 'unused' | 'missing';

const FILTER_MATCH: Record<Filter, (k: FoundryStatusKey) => boolean> = {
  all: () => true,
  'in-use': (k) => k === 'in-use',
  'switched-off': (k) => k === 'switched-off',
  unused: (k) => k === 'unused',
  missing: (k) => k === 'missing-named' || k === 'missing-stale',
};

const PILL: Record<FoundryTone, PillTone> = { success: 'green', warning: 'amber', error: 'red', default: 'slate' };

const mono = "'JetBrains Mono', monospace";

export const FoundryDeploymentsPage = observer(function FoundryDeploymentsPage() {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const screens = Grid.useBreakpoint();

  const [overview, setOverview] = useState<FoundryDeploymentsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<FoundryDeploymentRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverview(await getFoundryDeployments());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () => (overview?.rows ?? []).filter((r) => FILTER_MATCH[filter](r.statusKey)),
    [overview, filter],
  );

  const counts = overview?.counts;
  const missing = (counts?.missingAndNamed ?? 0) + (counts?.missingStale ?? 0);

  function pick(next: Filter): void {
    setFilter((current) => (current === next ? 'all' : next));
  }

  function open(row: FoundryDeploymentRow): void {
    setSelected(row);
    trackEvent('Foundry.DeploymentOpened', { name: row.name, status: row.statusKey });
  }

  const columns: ColumnsType<FoundryDeploymentRow> = [
    {
      title: 'Deployment',
      key: 'name',
      render: (_, r) => (
        <div style={{ minWidth: 180 }}>
          <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 13 }}>{r.name}</div>
          {r.existsInFoundry ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {[r.modelName, r.modelVersion].filter(Boolean).join(' · ')}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>Not deployed on this project</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Publisher',
      dataIndex: 'publisher',
      key: 'publisher',
      render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'SKU · capacity',
      key: 'sku',
      render: (_, r) =>
        r.skuName ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            {r.skuName}
            {r.capacity != null && (
              <Text type="secondary" style={{ fontFamily: mono, fontSize: 12 }}> · {r.capacity.toLocaleString()}</Text>
            )}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Can do',
      key: 'capabilities',
      render: (_, r) =>
        r.capabilities.length ? (
          <Space size={4} wrap>
            {r.capabilities.map((c) => (
              <Tag key={c} style={{ marginInlineEnd: 0 }}>{c}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => <StatusPill tone={PILL[r.tone]}>{r.statusLabel}</StatusPill>,
    },
    {
      title: 'What uses it',
      key: 'reading',
      render: (_, r) => <span style={{ fontSize: 13, lineHeight: 1.5 }}>{r.reading}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="amber"
        icon={<ClusterOutlined />}
        title="Deployments"
        subtitle={
          overview?.projectName
            ? `What the Foundry project ${overview.projectName} has deployed, and what in the platform uses each deployment. Read-only.`
            : 'What the Foundry project has deployed, and what in the platform uses each deployment. Read-only.'
        }
        count={counts?.deployed}
        countLabel={counts?.deployed === 1 ? 'deployment' : 'deployments'}
        actions={
          <Button icon={<ReloadOutlined />} loading={loading && !!overview} onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="Couldn't read the Foundry project"
          description={error}
          action={<Button size="small" onClick={() => void load()}>Retry</Button>}
        />
      )}

      {!overview && loading ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : overview && counts ? (
        <>
          {counts.missingAndNamed > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${counts.missingAndNamed} deployment${counts.missingAndNamed === 1 ? ' is' : 's are'} named by a switched-on row but not deployed`}
              description="Calls through those rows fail. Open the row to see which settings name it."
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            <KpiTile label="Deployed" value={counts.deployed} accent="ink" valueSize="medium" onClick={() => setFilter('all')} />
            <KpiTile label="In use" value={counts.inUse} accent="green" valueSize="medium" onClick={() => pick('in-use')} />
            <KpiTile label="Switched off" value={counts.switchedOff} accent="amber" valueSize="medium" onClick={() => pick('switched-off')} />
            <KpiTile label="Not used" value={counts.unused} accent="violet" valueSize="medium" onClick={() => pick('unused')} />
            <KpiTile
              label="Not in Foundry"
              value={missing}
              accent={counts.missingAndNamed > 0 ? 'red' : 'ink'}
              valueSize="medium"
              onClick={() => pick('missing')}
            />
          </div>

          <SectionCard
            title="Deployments and what names them"
            subtitle={overview.projectHost ?? undefined}
            noPadding
          >
            <div style={{ padding: '12px 16px', overflowX: 'auto' }}>
              <Segmented<Filter>
                size="small"
                value={filter}
                onChange={setFilter}
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'In use', value: 'in-use' },
                  { label: 'Switched off', value: 'switched-off' },
                  { label: 'Not used', value: 'unused' },
                  { label: 'Not in Foundry', value: 'missing' },
                ]}
              />
            </div>
            {rows.length === 0 ? (
              <BrandedEmpty size="sm" title="Nothing here" description="No deployment matches this filter." />
            ) : (
              <Table<FoundryDeploymentRow>
                rowKey="name"
                size="middle"
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 900 }}
                onRow={(r) => ({ onClick: () => open(r), style: { cursor: 'pointer' } })}
              />
            )}
          </SectionCard>

          <Text type="secondary" style={{ fontSize: 12, fontFamily: mono }}>
            Read at {new Date(overview.generatedAtUtc).toLocaleTimeString()} · this page only reads; searching the model
            catalog and deploying come next
          </Text>
        </>
      ) : null}

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        size={screens.sm ? 560 : '100%'}
        destroyOnClose
        styles={{ body: { padding: '18px 22px 28px' } }}
        title={
          selected && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: mono, fontWeight: 600 }}>{selected.name}</span>
              <StatusPill tone={PILL[selected.tone]}>{selected.statusLabel}</StatusPill>
            </span>
          )
        }
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{selected.reading}</div>

            {selected.existsInFoundry && (
              <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', margin: 0, fontSize: 13 }}>
                <dt style={{ color: colors.slate[500] }}>Model</dt>
                <dd style={{ margin: 0 }}>{[selected.modelName, selected.modelVersion].filter(Boolean).join(' · ') || '—'}</dd>
                <dt style={{ color: colors.slate[500] }}>Publisher</dt>
                <dd style={{ margin: 0 }}>{selected.publisher ?? '—'}</dd>
                <dt style={{ color: colors.slate[500] }}>SKU</dt>
                <dd style={{ margin: 0 }}>{selected.skuName ?? '—'}</dd>
                <dt style={{ color: colors.slate[500] }}>Capacity</dt>
                <dd style={{ margin: 0, fontFamily: mono }}>{selected.capacity?.toLocaleString() ?? '—'}</dd>
                <dt style={{ color: colors.slate[500] }}>Can do</dt>
                <dd style={{ margin: 0 }}>{selected.capabilities.join(', ') || '—'}</dd>
              </dl>
            )}

            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Rows that name it {selected.uses.length > 0 && <Text type="secondary">({selected.uses.length})</Text>}
              </div>
              {selected.uses.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>None.</Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selected.uses.map((u) => (
                    <div
                      key={`${u.kindLabel}-${u.rowId}`}
                      style={{
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        borderLeft: `3px solid ${u.isOn ? colors.green[500] : colors.slate[400]}`,
                        borderRadius: 8,
                        padding: '8px 12px',
                        background: isDark ? colors.slate[800] : '#FFFFFF',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <Text strong style={{ fontSize: 12.5 }}>{u.kindLabel}</Text>
                        <StatusPill tone={u.isOn ? 'green' : 'slate'}>{u.isOn ? 'On' : 'Off'}</StatusPill>
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5, color: isDark ? colors.slate[300] : colors.slate[700] }}>
                        {u.reading}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
});
